import React, { useState, useEffect, useRef, JSX } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Toast } from 'primereact/toast';
import * as echarts from 'echarts';
import apiClient from '../../stateManagers/services/axiosInterceptor';
import GlobalHeaderTitle from '../../components/GlobalHeaderTitle';
import ProfilePictureHover from '../../components/ProfilePictureHover';
import { useSchoolDetailsStore } from '../../stateManagers/hooks/useSchoolDetailsStore';
import ClassSectionFilter from '../Students/parts/ClassSectionFilter';
import ClassAttendanceSkeletonLoader from '../../skeletons/ClassAttendanceSkeletonLoader';

interface ClassData {
  id: string;
  class_name: string;
  sections: string[];
}

interface AttendanceSummary {
  total_students: number;
  present: number;
  absent: number;
  leave: number;
  half_day: number;
  skipped: number;
  attendance_percentage: number;
}

interface ClassAttendanceStudentRow {
  student_id: string;
  name: string;
  roll_no: number;
  admission_no: string;
  gender: string;
  profile_picture_url: string;
  status: string; // present / absent / leave / half_day / skipped
  remarks?: string | null;
}

interface ClassAttendanceDetailData {
  class_id: string;
  class_name: string;
  section: string;
  date: string;
  session_id: string;
  summary: AttendanceSummary;
  students: ClassAttendanceStudentRow[];
}

// Same semantic colors already used elsewhere in the app for these kinds
// of status badges (green = good, red = delete/negative, amber = swap/
// pending, purple = secondary action, gray = neutral/skipped).
const STATUS_COLORS: Record<string, string> = {
  present: '#10b981',
  absent: '#ef4444',
  leave: '#f59e0b',
  half_day: '#8b5cf6',
  skipped: '#9ca3af',
};

const STATUS_LABELS: Record<string, string> = {
  present: 'Present',
  absent: 'Absent',
  leave: 'Leave',
  half_day: 'Half Day',
  skipped: 'Skipped',
};

// Same top→bottom gradient treatment as the fees-summary pie (paid/unpaid/
// total), just keyed to attendance statuses instead.
const STATUS_GRADIENTS: Record<string, [string, string]> = {
  present: ['#22c55e', '#16a34a'],
  absent: ['#ef4444', '#dc2626'],
  leave: ['#fbbf24', '#f59e0b'],
  half_day: ['#a78bfa', '#8b5cf6'],
  skipped: ['#cbd5e1', '#94a3b8'],
};

// Idle/hover gradient pairs for the horizontal status bars — same recipe
// (light→saturated idle, saturated→dark hover, matching soft shadow) as
// the segments in UnmarkedClassesChart, just oriented left-to-right.
const STATUS_BAR_STYLE: Record<
  string,
  { idle: string; hover: string; idleShadow: string; hoverShadow: string; textColor: string }
> = {
  present: {
    idle: 'linear-gradient(90deg, #bbf7d0 0%, #4ade80 100%)',
    hover: 'linear-gradient(90deg, #4ade80 0%, #15803d 100%)',
    idleShadow: '0 3px 8px rgba(34, 197, 94, 0.2)',
    hoverShadow: '0 6px 14px rgba(21, 128, 61, 0.35)',
    textColor: '#16a34a',
  },
  absent: {
    idle: 'linear-gradient(90deg, #fecaca 0%, #f87171 100%)',
    hover: 'linear-gradient(90deg, #f87171 0%, #b91c1c 100%)',
    idleShadow: '0 3px 8px rgba(239, 68, 68, 0.18)',
    hoverShadow: '0 6px 14px rgba(185, 28, 28, 0.35)',
    textColor: '#dc2626',
  },
  leave: {
    idle: 'linear-gradient(90deg, #fde68a 0%, #f59e0b 100%)',
    hover: 'linear-gradient(90deg, #f59e0b 0%, #b45309 100%)',
    idleShadow: '0 3px 8px rgba(245, 158, 11, 0.2)',
    hoverShadow: '0 6px 14px rgba(180, 83, 9, 0.35)',
    textColor: '#b45309',
  },
  half_day: {
    idle: 'linear-gradient(90deg, #ddd6fe 0%, #a78bfa 100%)',
    hover: 'linear-gradient(90deg, #a78bfa 0%, #6d28d9 100%)',
    idleShadow: '0 3px 8px rgba(167, 139, 250, 0.2)',
    hoverShadow: '0 6px 14px rgba(109, 40, 217, 0.35)',
    textColor: '#6d28d9',
  },
  skipped: {
    idle: 'linear-gradient(90deg, #e2e8f0 0%, #94a3b8 100%)',
    hover: 'linear-gradient(90deg, #94a3b8 0%, #475569 100%)',
    idleShadow: '0 3px 8px rgba(100, 116, 139, 0.18)',
    hoverShadow: '0 6px 14px rgba(71, 85, 105, 0.35)',
    textColor: '#64748b',
  },
};

// Local (not UTC) date parts — avoids the toISOString() day-shift bug for
// users west of UTC.
const toLocalDateStr = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// ==================== ECHARTS ATTENDANCE PIE ====================
// Mirrors the fees-summary donut: gradient fills, rounded segment borders,
// soft drop shadow, count-in-segment labels, and a bottom legend with the
// raw counts appended to each label.
const EChartsAttendancePie: React.FC<{ summary: AttendanceSummary }> = ({ summary }) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;

    const chart = echarts.init(chartRef.current);

    const keys = (['present', 'absent', 'leave', 'half_day', 'skipped'] as const).filter(
      (k) => summary[k] > 0
    );

    const option = {
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)',
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        borderColor: '#e2e8f0',
        borderWidth: 1,
        borderRadius: 12,
        textStyle: { color: '#1e40af', fontSize: 13, fontWeight: '600' },
        extraCssText: 'box-shadow: 0 8px 32px rgba(59, 130, 246, 0.2);',
        padding: [8, 12],
      },
      legend: {
        orient: 'horizontal',
        bottom: 0,
        left: 'center',
        textStyle: { color: '#1e40af', fontSize: 11, fontWeight: '600' },
        formatter: (name: string) => {
          const key = keys.find((k) => STATUS_LABELS[k] === name);
          return `${name}: ${key ? summary[key] : 0}`;
        },
        itemGap: 12,
        itemWidth: 10,
        itemHeight: 10,
        icon: 'circle',
      },
      series: [
        {
          type: 'pie',
          radius: ['52%', '76%'],
          center: ['50%', '42%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 10,
            borderColor: '#fff',
            borderWidth: 2,
            shadowBlur: 8,
            shadowColor: 'rgba(0, 0, 0, 0.18)',
          },
          label: {
            show: true,
            position: 'inside',
            formatter: '{c}',
            fontSize: 13,
            fontWeight: 'bold',
            color: '#fff',
            textShadowColor: 'rgba(0, 0, 0, 0.4)',
            textShadowBlur: 3,
            textShadowOffsetX: 1,
            textShadowOffsetY: 1,
          },
          emphasis: {
            itemStyle: { shadowBlur: 20, shadowOffsetX: 0, shadowColor: 'rgba(0, 0, 0, 0.3)' },
            label: { fontSize: 15, fontWeight: 'bold' },
          },
          data: keys.map((k) => {
            const [from, to] = STATUS_GRADIENTS[k];
            return {
              value: summary[k],
              name: STATUS_LABELS[k],
              itemStyle: {
                color: {
                  type: 'linear',
                  x: 0,
                  y: 0,
                  x2: 0,
                  y2: 1,
                  colorStops: [
                    { offset: 0, color: from },
                    { offset: 1, color: to },
                  ],
                },
              },
            };
          }),
        },
      ],
    };

    chart.setOption(option);
    const handleResize = () => chart.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [summary]);

  return <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: '196px' }} />;
};

// Blue/teal pair for the two roll-up figures (Total Students, Attendance %)
// — visually distinct from the five status colors above, no purple.
const SUMMARY_BAR_STYLE: Record<
  string,
  { idle: string; hover: string; idleShadow: string; hoverShadow: string; textColor: string }
> = {
  total: {
    idle: 'linear-gradient(180deg, #bfdbfe 0%, #3b82f6 100%)',
    hover: 'linear-gradient(180deg, #3b82f6 0%, #1e40af 100%)',
    idleShadow: '0 3px 8px rgba(59, 130, 246, 0.2)',
    hoverShadow: '0 6px 14px rgba(30, 64, 175, 0.35)',
    textColor: '#1e40af',
  },
  attendance_pct: {
    idle: 'linear-gradient(180deg, #99f6e4 0%, #14b8a6 100%)',
    hover: 'linear-gradient(180deg, #14b8a6 0%, #0f766e 100%)',
    idleShadow: '0 3px 8px rgba(20, 184, 166, 0.2)',
    hoverShadow: '0 6px 14px rgba(15, 118, 110, 0.35)',
    textColor: '#0f766e',
  },
};

// ==================== ANIMATED STATUS BARS ====================
// Same growing-gradient-bar language as UnmarkedClassesChart — one
// horizontal bar per status on the left, animated width on mount,
// idle→hover gradient shift, matching soft shadow. Total students and
// attendance % now sit as two vertical column bars on the OPPOSITE side
// (right, past a vertical divider) rather than stacked below the status
// list.
const AttendanceStatusBars: React.FC<{ summary: AttendanceSummary }> = ({ summary }) => {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    setAnimated(false);
    const t = setTimeout(() => setAnimated(true), 50);
    return () => clearTimeout(t);
  }, [summary]);

  const total = Math.max(summary.total_students, 1);
  const statusRows = (['present', 'absent', 'leave', 'half_day', 'skipped'] as const).map((key) => ({
    key,
    label: STATUS_LABELS[key],
    value: String(summary[key]),
    pct: (summary[key] / total) * 100,
    ...STATUS_BAR_STYLE[key],
  }));

  const summaryColumns = [
    {
      key: 'total',
      label: 'Total Students',
      value: String(summary.total_students),
      pct: 100,
      ...SUMMARY_BAR_STYLE.total,
    },
    {
      key: 'attendance_pct',
      label: 'Attendance %',
      value: `${summary.attendance_percentage}%`,
      pct: Math.min(Math.max(summary.attendance_percentage, 0), 100),
      ...SUMMARY_BAR_STYLE.attendance_pct,
    },
  ];

  const renderHorizontalBar = (r: {
    key: string;
    label: string;
    value: string;
    pct: number;
    idle: string;
    hover: string;
    idleShadow: string;
    hoverShadow: string;
    textColor: string;
  }) => {
    const isHovered = hoveredKey === r.key;
    const width = animated ? r.pct : 0;
    return (
      <div key={r.key} onMouseEnter={() => setHoveredKey(r.key)} onMouseLeave={() => setHoveredKey(null)}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#475569' }}>{r.label}</span>
          <span style={{ fontSize: 12, fontWeight: 800, color: r.textColor }}>{r.value}</span>
        </div>
        <div style={{ background: '#f1f5f9', borderRadius: 999, height: 10, overflow: 'hidden' }}>
          <div
            style={{
              width: `${width}%`,
              height: '100%',
              borderRadius: 999,
              background: isHovered ? r.hover : r.idle,
              boxShadow: r.pct > 0 ? (isHovered ? r.hoverShadow : r.idleShadow) : 'none',
              transition:
                'width 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.25s ease, box-shadow 0.25s ease',
            }}
          />
        </div>
      </div>
    );
  };

  const renderColumnBar = (r: {
    key: string;
    label: string;
    value: string;
    pct: number;
    idle: string;
    hover: string;
    idleShadow: string;
    hoverShadow: string;
    textColor: string;
  }) => {
    const isHovered = hoveredKey === r.key;
    const h = animated ? r.pct : 0;
    return (
      <div
        key={r.key}
        onMouseEnter={() => setHoveredKey(r.key)}
        onMouseLeave={() => setHoveredKey(null)}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 64, height: '100%' }}
      >
        <div style={{ fontSize: 13, fontWeight: 800, color: r.textColor, marginBottom: 6 }}>{r.value}</div>
        <div
          style={{
            width: 30,
            flex: '1 1 auto',
            minHeight: 0,
            background: '#f1f5f9',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'flex-end',
            overflow: 'hidden',
            transform: isHovered ? 'scale(1.08)' : 'scale(1)',
            transformOrigin: 'bottom center',
            transition: 'transform 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }}
        >
          <div
            style={{
              width: '100%',
              height: `${h}%`,
              borderRadius: '6px 6px 0 0',
              background: isHovered ? r.hover : r.idle,
              boxShadow: r.pct > 0 ? (isHovered ? r.hoverShadow : r.idleShadow) : 'none',
              transition:
                'height 0.7s cubic-bezier(0.34, 1.56, 0.64, 1), background 0.25s ease, box-shadow 0.25s ease',
            }}
          />
        </div>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', marginTop: 6, textAlign: 'center' }}>
          {r.label}
        </div>
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', gap: 20, alignItems: 'stretch' }}>
      {/* Status Summary — vertical stack of horizontal bars, left side */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, flex: '1 1 auto', minWidth: 0 }}>
        {statusRows.map(renderHorizontalBar)}
      </div>

      <div style={{ alignSelf: 'stretch', borderLeft: '1px dashed #e2e8f0' }} />

      {/* Total Students / Attendance % — flexed opposite to Status Summary,
          stretched to the same height as the status bars beside them */}
      <div style={{ display: 'flex', gap: 20, flex: '0 0 auto', alignItems: 'stretch' }}>
        {summaryColumns.map(renderColumnBar)}
      </div>
    </div>
  );
};


const ClassAttendanceOverview: React.FC = () => {
  const toast = useRef<Toast>(null);

  const schoolDetails = useSchoolDetailsStore((state) => state.schoolDetails);
  const currentSessionId = schoolDetails?.current_session?.id || null;

  const [classes, setClasses] = useState<ClassData[]>([]);
  const [selectedClass, setSelectedClass] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  const [loading, setLoading] = useState<boolean>(false);
  const [detail, setDetail] = useState<ClassAttendanceDetailData | null>(null);

  const handleClassesLoaded = (loadedClasses: ClassData[]) => setClasses(loadedClasses);

  const selectedClassHasSections = (): boolean => {
    if (!selectedClass) return false;
    const cls = classes.find((c) => c.id === selectedClass);
    return cls ? Boolean(cls.sections?.length) : false;
  };

  const shouldFetch = (): boolean => {
    if (!currentSessionId || !selectedClass || !selectedDate) return false;
    if (selectedClassHasSections()) return selectedSection !== null;
    return true;
  };

  const handleClassChange = (classId: string | null) => {
    setSelectedClass(classId);
    setSelectedSection(null);
    setDetail(null);
  };

  const handleSectionChange = (section: string | null) => {
    setSelectedSection(section);
    setDetail(null);
  };

  const fetchDetail = async (): Promise<void> => {
    if (!shouldFetch() || !selectedClass || !currentSessionId) {
      setDetail(null);
      return;
    }
    setLoading(true);
    try {
      const params: any = {
        class_id: selectedClass,
        date: toLocalDateStr(selectedDate),
        session_id: currentSessionId,
      };
      if (selectedSection) params.section = selectedSection;

      const response = await apiClient.get('/api/admin/class-attendance-detail', { params });
      const data = response.data;

      if (data.status === 'success') {
        setDetail(data.data);
      } else {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to fetch class attendance detail',
          life: 4000,
        });
      }
    } catch (err: any) {
      toast.current?.show({
        severity: 'error',
        summary: 'Network Error',
        detail: err.response?.data?.message || 'An error occurred while fetching attendance detail',
        life: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClass, selectedSection, selectedDate, currentSessionId]);

  const summary = detail?.summary;
  const hasRoster = !!summary && summary.total_students > 0;

  const serialNumberTemplate = (_: ClassAttendanceStudentRow, options: ColumnBodyOptions): JSX.Element => (
    <div
      style={{
        width: 28,
        height: 28,
        background: '#f9fafb',
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 700,
        fontSize: 12,
        color: '#6b7280',
        margin: '0 auto',
      }}
    >
      {options.rowIndex + 1}
    </div>
  );

  const studentNameTemplate = (rowData: ClassAttendanceStudentRow): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <ProfilePictureHover src={rowData.profile_picture_url} name={rowData.name} cacheBust={rowData.student_id} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 600,
            fontSize: 13,
            color: '#1f2937',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {rowData.name}
        </div>
        <div style={{ fontSize: 11, color: '#9ca3af' }}>{rowData.admission_no}</div>
      </div>
    </div>
  );

  const rollNoTemplate = (r: ClassAttendanceStudentRow) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 32,
        padding: '4px 8px',
        borderRadius: 6,
        background: '#f9fafb',
        fontSize: 12,
        fontWeight: 700,
        color: '#1f2937',
        border: '1px solid #e5e7eb',
      }}
    >
      {r.roll_no}
    </div>
  );

  const genderTemplate = (r: ClassAttendanceStudentRow) => (
    <div
      style={{
        fontSize: 13,
        fontWeight: 600,
        color: r.gender?.toLowerCase() === 'male' ? '#3b82f6' : '#ec4899',
        textTransform: 'capitalize',
      }}
    >
      {r.gender || '—'}
    </div>
  );

  const statusTemplate = (r: ClassAttendanceStudentRow) => {
    const color = STATUS_COLORS[r.status] || '#9ca3af';
    const label = STATUS_LABELS[r.status] || r.status;
    return (
      <div
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '4px 10px',
          borderRadius: 999,
          background: `${color}1a`,
          fontSize: 12,
          fontWeight: 700,
          color,
        }}
      >
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: color }} />
        {label}
      </div>
    );
  };

  const remarksTemplate = (r: ClassAttendanceStudentRow) => (
    <div style={{ fontSize: 13, color: r.remarks ? '#1f2937' : '#9ca3af' }}>{r.remarks || '—'}</div>
  );

  const getEmptyMessage = (): string => {
    if (!currentSessionId) return 'Could not determine the current session for this school.';
    if (!selectedClass) return 'Select a class above to view its attendance.';
    if (selectedClassHasSections() && !selectedSection) return 'Select a section to view attendance.';
    return 'No students found for this class/section.';
  };

  return (
    <>
      <Toast ref={toast} position="top-right" />

      <div style={{ minHeight: '100vh', fontFamily: 'system-ui,-apple-system,sans-serif' }}>
        <GlobalHeaderTitle
          title="Class Attendance Overview"
          subtitle={detail ? `${detail.class_name}${detail.section ? ' - ' + detail.section : ''} · ${detail.date}` : 'Select a class and date'}
          icon="pi pi-chart-pie"
          buttons={[]}
        />

        <ClassSectionFilter
          selectedClass={selectedClass}
          selectedSection={selectedSection}
          onClassChange={handleClassChange}
          onSectionChange={handleSectionChange}
          onClassesLoaded={handleClassesLoaded}
        />

        {loading ? (
          <ClassAttendanceSkeletonLoader />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: 16,
                alignItems: 'stretch',
                flexWrap: 'wrap',
              }}
            >
              {/* Calendar */}
              <div
                style={{
                  background: 'white',
                  borderRadius: 16,
                  boxShadow: '0 2px 10px rgba(0,0,0,.06)',
                  border: '1px solid #e5e7eb',
                  padding: 10,
                  flex: '0 0 auto',
                }}
              >
                <style>{`
                  .p-datepicker { border: none !important; padding: 0 !important; font-size: 12px !important; }
                  .p-datepicker table td { padding: 1px !important; }
                  .p-datepicker table td > span { width: 26px !important; height: 26px !important; font-size: 12px !important; }
                  .p-datepicker-header { padding: 4px 2px !important; }
                  .p-datepicker-header .p-datepicker-title { font-size: 12px !important; }
                `}</style>
                <Calendar
                  value={selectedDate}
                  onChange={(e) => e.value && setSelectedDate(e.value as Date)}
                  maxDate={new Date()}
                  inline
                />
              </div>

              {!detail ? (
                <div
                  style={{
                    flex: 1,
                    minWidth: 260,
                    background: 'white',
                    borderRadius: 16,
                    boxShadow: '0 2px 10px rgba(0,0,0,.06)',
                    border: '1px solid #e5e7eb',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    color: '#64748b',
                    fontWeight: 500,
                    padding: 20,
                  }}
                >
                  {getEmptyMessage()}
                </div>
              ) : (
                <>
                  {/* Status bars card */}
                  <div
                    style={{
                      background: 'white',
                      borderRadius: 16,
                      boxShadow: '0 2px 10px rgba(0,0,0,.06)',
                      border: '1px solid #e5e7eb',
                      padding: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      flex: '1 1 320px',
                      minWidth: 260,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 10 }}>
                      Status Summary
                    </div>
                    {summary && hasRoster ? (
                      <AttendanceStatusBars summary={summary} />
                    ) : (
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#9ca3af',
                          fontSize: 12,
                          fontWeight: 500,
                          textAlign: 'center',
                          padding: '20px 8px',
                        }}
                      >
                        No students enrolled in this class/section.
                      </div>
                    )}
                  </div>

                  {/* Pie chart card */}
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #f8faff 0%, #f5f7ff 100%)',
                      borderRadius: 16,
                      boxShadow: '0 2px 10px rgba(0,0,0,.06)',
                      border: '1px solid rgba(59,130,246,.08)',
                      padding: 14,
                      display: 'flex',
                      flexDirection: 'column',
                      flex: '1 1 276px',
                      minWidth: 253,
                      maxWidth: 322,
                    }}
                  >
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1e40af', marginBottom: 6 }}>
                      Attendance Breakdown
                    </div>
                    <div style={{ flex: 1, minHeight: 196, paddingTop: 16, position: 'relative' }}>
                      {summary && hasRoster ? (
                        <EChartsAttendancePie summary={summary} />
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#9ca3af',
                            fontSize: 12,
                            fontWeight: 500,
                            textAlign: 'center',
                            padding: '0 8px',
                          }}
                        >
                          No students enrolled in this class/section.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Student list */}
            {detail && (
              <div
                style={{
                  background: 'white',
                  borderRadius: 20,
                  boxShadow: '0 2px 12px rgba(0,0,0,.06)',
                  border: '1px solid #e5e7eb',
                  overflow: 'hidden',
                }}
              >
                <style>{`
                  .p-datatable .p-datatable-thead > tr > th { background:#f9fafb!important; color:#6b7280!important; font-size:11px!important; font-weight:700!important; text-transform:uppercase!important; letter-spacing:.8px!important; padding:16px 12px!important; border-bottom:1px solid #e5e7eb!important; }
                  .p-datatable .p-datatable-tbody > tr > td { padding:16px 12px!important; border-bottom:1px solid #f3f4f6!important; }
                  .p-datatable .p-datatable-tbody > tr:last-child > td { border-bottom:none!important; }
                `}</style>
                <DataTable value={detail.students} emptyMessage="No students found for this class/section.">
                  <Column header="#" body={serialNumberTemplate} style={{ width: 52 }} />
                  <Column header="Student Name" body={studentNameTemplate} />
                  <Column header="Gender" body={genderTemplate} style={{ width: 90 }} />
                  <Column header="Roll No" body={rollNoTemplate} style={{ width: 80 }} />
                  <Column header="Status" body={statusTemplate} style={{ width: 130 }} />
                  <Column header="Remarks" body={remarksTemplate} />
                </DataTable>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default ClassAttendanceOverview;